import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { PublicProfile as Profile } from "@gooner/shared";
import { api, ApiHttpError } from "../../lib/api";
import { useSession } from "../../lib/session";
import { GoldName } from "../ui/Gold";
import { Masked } from "../../lib/replayMask";
import { ProfileBody } from "../profile/ProfileBody";
import {
  CollectionCard,
  CollectionGrid,
  fromPublicPlaylist,
} from "../collections/CollectionCard";

/**
 * `/u/:username` — a lord's page, and where the share-card QR lands.
 *
 * When the owner's profile switch is on, this renders the SAME body as /profile: the
 * full stats, the highlights, the heatmap. What it never mounts is ShareCardButton —
 * the card is the owner's to make and publish, and a page that cannot offer the
 * download has no reason to offer the preview either. The visitor's door to a card is
 * the "See your own stats" CTA below, which is what the card's own caption promises.
 *
 * When the switch is off, a visitor gets the name, the GOLD chip and the published
 * collections, and nothing else. Per-user activity is the most sensitive data the app
 * holds and this page answers to anyone who types a name, so `stats` arriving null is
 * a refusal to honour, never a loading state to retry. The owner still sees their own
 * page either way — a switch that hides your page from you reads as a bug.
 */
export function PublicProfile() {
  const { username = "" } = useParams();
  const user = useSession((s) => s.user);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setProfile(null);
    setMissing(false);
    setError(null);
    api
      .publicProfile(username)
      .then((r) => live && setProfile(r.profile))
      .catch((e: unknown) => {
        if (!live) return;
        // 404 gets its own page; anything else is a real failure and says so, rather
        // than sitting on "Loading…" forever waiting for a reload that never comes.
        if (e instanceof ApiHttpError && e.status === 404) setMissing(true);
        else setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      live = false;
    };
  }, [username, user?.username]);

  if (missing) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-xl font-semibold text-zinc-100">No lord by that name.</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Whoever this card belonged to has moved on — the catalog hasn't.
        </p>
        <Link
          to="/browse"
          className="mt-6 inline-block rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          Browse the catalog
        </Link>
      </main>
    );
  }

  if (error) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="text-sm text-red-400">{error}</p>
      </main>
    );
  }

  if (!profile) {
    return <main className="mx-auto max-w-5xl px-4 py-16 text-center text-sm text-zinc-500">Loading…</main>;
  }

  const isOwn = user?.username.toLowerCase() === profile.username.toLowerCase();
  const since = new Date(profile.joinedAt).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  return (
    <main className="mx-auto max-w-5xl space-y-8 px-4 py-8">
      {profile.stats ? (
        <>
          {/* The owner reaching their own page while it is shut: show it, and say who
              else can see it, so the switch's state is legible from the page it governs. */}
          {isOwn && !profile.isPublic ? (
            <p className="rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] px-4 py-3 text-sm text-zinc-300">
              Your profile is private — only you see this.{" "}
              <Link to="/profile" className="text-[var(--color-accent-soft)] hover:underline">
                Make it public
              </Link>
              .
            </p>
          ) : null}
          <ProfileBody
            username={profile.username}
            gold={profile.gold}
            avatarUrl={profile.avatarUrl}
            stats={profile.stats}
            isOwner={isOwn}
          />
        </>
      ) : (
        <header>
          <h1 className="flex items-center text-2xl font-semibold text-zinc-100">
            <GoldName name={profile.username} gold={profile.gold} />
          </h1>
          <p className="mt-1 text-sm text-zinc-500">Gooning since {since}</p>
          <p className="mt-3 text-sm text-zinc-400">
            This lord keeps their stats private.
          </p>
        </header>
      )}

      {profile.collections.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-400">
            Published collections
          </h2>
          <CollectionGrid>
            {profile.collections.map((c) => (
              <CollectionCard key={c.id} collection={fromPublicPlaylist(c)} to={`/browse/${c.id}`} />
            ))}
          </CollectionGrid>
        </section>
      ) : (
        <p className="text-sm text-zinc-500">Nothing published yet.</p>
      )}

      {/* The card's caption promised the scanner their OWN stats — this is that door.
          /profile is sign-in gated, so a stranger lands on the auth prompt, which is
          the honest next step for someone who doesn't have numbers yet. */}
      {!isOwn ? (
        <aside className="rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-5">
          <p className="text-sm text-zinc-300">
            This is <Masked>{profile.username}</Masked>'s page. Your walls, faps and edges earn a card of
            your own.
          </p>
          <Link
            to="/profile"
            className="mt-3 inline-block rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            See your own stats
          </Link>
        </aside>
      ) : null}
    </main>
  );
}
